"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useMotionValueEvent, useScroll } from "motion/react";

/**
 * SlideUpInOut Animation Component
 *
 * Wraps children in a motion.div that animates based on scroll direction:
 * - Scrolling down + entering: slides up in
 * - Scrolling down + leaving: slides up out
 * - Scrolling up + entering: slides down in
 * - Scrolling up + leaving: slides down out
 */

interface SlideUpInOutProps {
  /**
   * Content to be animated
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;
  /**
   * Animation offset in pixels
   */
  offset?: number;
  /**
   * Animation duration in seconds
   */
  duration?: number;
  /**
   * Animation delay in seconds
   */
  delay?: number;
}
export const SlideUpInOut: React.FC<SlideUpInOutProps> = t0 => {
  const $ = _c(23);
  const {
    children,
    className,
    offset: t1,
    duration: t2,
    delay: t3
  } = t0;
  const offset = t1 === undefined ? 80 : t1;
  const duration = t2 === undefined ? 0.3 : t2;
  const delay = t3 === undefined ? 0 : t3;
  const controls = useAnimation();
  const {
    scrollY
  } = useScroll();
  const directionRef = useRef("down");
  const [isInView, setIsInView] = useState(false);
  const hasEntered = useRef(false);
  let t4;
  if ($[0] !== scrollY) {
    t4 = current => {
      const previous = scrollY.getPrevious() ?? 0;
      directionRef.current = current > previous ? "down" : "up";
    };
    $[0] = scrollY;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  useMotionValueEvent(scrollY, "change", t4);
  let t5;
  let t6;
  if ($[2] !== controls || $[3] !== delay || $[4] !== duration || $[5] !== isInView || $[6] !== offset) {
    t5 = () => {
      if (!isInView) {
        if (!hasEntered.current) {
          return;
        }
        if (directionRef.current === "down") {
          controls.start({
            y: -offset,
            opacity: 0,
            transition: {
              duration,
              delay
            }
          });
        } else {
          controls.start({
            y: offset,
            opacity: 0,
            transition: {
              duration,
              delay
            }
          });
        }
        return;
      }
      if (directionRef.current === "down") {
        controls.start({
          y: 0,
          opacity: 1,
          transition: {
            duration,
            delay
          }
        });
      } else {
        controls.start({
          y: 0,
          opacity: 1,
          transition: {
            duration,
            delay
          }
        });
      }
    };
    t6 = [isInView, controls, offset, duration, delay, directionRef];
    $[2] = controls;
    $[3] = delay;
    $[4] = duration;
    $[5] = isInView;
    $[6] = offset;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== controls || $[10] !== offset) {
    t7 = () => {
      if (!hasEntered.current) {
        hasEntered.current = true;
      } else {
        if (directionRef.current === "down") {
          controls.set({
            y: offset,
            opacity: 0
          });
        } else {
          controls.set({
            y: -offset,
            opacity: 0
          });
        }
      }
      setIsInView(true);
    };
    $[9] = controls;
    $[10] = offset;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleViewportEnter = t7;
  let t8;
  if ($[12] !== offset) {
    t8 = {
      y: offset,
      opacity: 0
    };
    $[12] = offset;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      amount: 0.3
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsInView(false);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      willChange: "transform, opacity"
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== className || $[19] !== controls || $[20] !== handleViewportEnter || $[21] !== t8) {
    t12 = <motion.div initial={t8} animate={controls} viewport={t9} onViewportEnter={handleViewportEnter} onViewportLeave={t10} className={className} style={t11}>{children}</motion.div>;
    $[17] = children;
    $[18] = className;
    $[19] = controls;
    $[20] = handleViewportEnter;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};